import { useCallback, useEffect, useRef, useState } from "react";
import { STICKY_BANNER_RULE_TYPES } from "../configs/stickyBanner/stickyBannerConfig.types";

function clamp01(n) {
  if (!Number.isFinite(n)) return 0;
  return Math.min(1, Math.max(0, n));
}

/**
 * Tracks scroll-based signals for sticky banner configs (PRIORITY scroll resolution).
 * @param {object} options
 * @param {boolean} options.enabled
 * @param {Array} options.scrollTriggers
 * @param {number} [options.scrollRatioDebounceMs] reduces noisy ratio updates (e.g. 100)
 */
export function useStickyBannerScrollTriggers({ enabled, scrollTriggers, scrollRatioDebounceMs = 0 }) {
  const [activeScrollTriggerIds, setActiveScrollTriggerIds] = useState(() => new Set());
  const [scrollRatio, setScrollRatio] = useState(0);
  const visibilityRef = useRef(new Map());
  const debounceTimerRef = useRef(0);
  const pendingRatioRef = useRef(0);

  const publishVisibility = useCallback(() => {
    const active = new Set();
    visibilityRef.current.forEach((isOn, id) => {
      if (isOn) active.add(id);
    });
    setActiveScrollTriggerIds(active);
  }, []);

  const flushScrollRatio = useCallback(() => {
    setScrollRatio(pendingRatioRef.current);
  }, []);

  const scheduleScrollRatio = useCallback(
    (ratio) => {
      pendingRatioRef.current = ratio;
      if (!scrollRatioDebounceMs) {
        setScrollRatio(ratio);
        return;
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = window.setTimeout(() => {
        debounceTimerRef.current = 0;
        flushScrollRatio();
      }, scrollRatioDebounceMs);
    },
    [scrollRatioDebounceMs, flushScrollRatio]
  );

  const updateScrollRatio = useCallback(() => {
    const el = document.documentElement;
    const scrollHeight = el.scrollHeight - window.innerHeight;
    const ratio = scrollHeight > 0 ? clamp01(window.scrollY / scrollHeight) : 0;
    scheduleScrollRatio(ratio);
  }, [scheduleScrollRatio]);

  useEffect(() => {
    if (!enabled) {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      visibilityRef.current = new Map();
      setActiveScrollTriggerIds(new Set());
      setScrollRatio(0);
      return undefined;
    }

    const elementTriggers = (scrollTriggers ?? []).filter(
      (t) => t?.type === STICKY_BANNER_RULE_TYPES.SCROLL_ELEMENT && t.targetElementId
    );

    const observers = [];

    const bindObservers = () => {
      [...observers].forEach((o) => o.disconnect());
      observers.length = 0;
      visibilityRef.current = new Map();

      for (const t of elementTriggers) {
        const node = document.getElementById(t.targetElementId);
        if (!node) continue;

        const threshold = typeof t.threshold === "number" ? t.threshold : 0.2;
        const obs = new IntersectionObserver(
          ([entry]) => {
            const visible = entry.isIntersecting && entry.intersectionRatio >= threshold;
            visibilityRef.current.set(t.id, visible);
            publishVisibility();
          },
          { root: null, rootMargin: t.rootMargin ?? "0px", threshold: [0, threshold, 1] }
        );
        obs.observe(node);
        observers.push(obs);
      }

      publishVisibility();
    };

    bindObservers();

    let raf = 0;
    const onScrollOrResize = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        updateScrollRatio();
      });
    };

    updateScrollRatio();
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize, { passive: true });

    const retryId = requestAnimationFrame(() => {
      bindObservers();
      updateScrollRatio();
    });

    return () => {
      cancelAnimationFrame(retryId);
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
      if (raf) cancelAnimationFrame(raf);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      observers.forEach((o) => o.disconnect());
    };
  }, [enabled, scrollTriggers, publishVisibility, updateScrollRatio]);

  return { activeScrollTriggerIds, scrollRatio };
}
